import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { addToCart } from "../../store/slices/cartSlice";
import { toggleFavorite } from "../../store/slices/favoritesSlice";
import { formatPrice } from "../../utils/priceUtils";
import { apiRequestJson } from "../../api/apiService";
import { selectProductById } from "../../store/slices/globalDataSlice";

/**
 * Modal de detalhes do produto com layout responsivo e carrossel de imagens otimizado.
 * Exibe carrossel quando há múltiplas imagens, informações completas e ações de favorito/carrinho.
 * Botões seguem padrão da aplicação: ícone em mobile, texto em desktop.
 * Descrição com scroll automático quando necessário.
 * 
 * Lógica de exibição de imagens do carrossel:
 * 1. Caso não existam imagens adicionais: exibe apenas a imagem principal
 * 2. Caso não existam imagens (principal e adicionais): exibe imagem padrão ("/vika_icon_light.gif")
 * 3. Caso existam imagens adicionais: exibe imagem principal primeiro, depois as adicionais
 * 4. Remove automaticamente imagens duplicadas (mesma URL) do carrossel
 * 5. Sistema de buffer: pré-carrega todas as imagens em segundo plano para navegação rápida
 * 6. Exibe primeira imagem imediatamente, outras carregam em background
 * 7. Tamanho fixo: todas as imagens mantêm dimensões consistentes no carrossel
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.open - Se o modal está visível
 * @param {Function} props.onClose - Callback para fechar o modal
 * @param {Object} props.product - Dados do produto
 * @param {string|number} props.product.id - ID único do produto
 * @param {string} props.product.nome - Nome do produto
 * @param {string} props.product.marca - Marca do produto
 * @param {number} props.product.preco - Preço do produto
 * @param {string} props.product.imagem - URL da imagem principal do produto
 * @param {string} props.product.categoria - Categoria do produto
 * @param {string} props.product.descricao - Descrição detalhada do produto
 * @returns {JSX.Element} Modal responsivo com carrossel e detalhes completos do produto
 */
function ProductModal({ open, onClose, product }) {
  const dispatch = useDispatch();
  const favorites = useSelector((state) => state.favorites.items);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageBuffer, setImageBuffer] = useState(new Map());
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  // Reset do índice quando o modal abre ou produto muda
  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
      setImageBuffer(new Map());
    }
  }, [open, product?.id]);
  
  // Função para pré-carregar imagens no buffer
  const preloadImages = (imageUrls) => {
    const newBuffer = new Map();
    
    imageUrls.forEach((url, index) => {
      const img = new Image();
      img.onload = () => {
        newBuffer.set(url, img.src);
        setImageBuffer(prev => new Map([...prev, [url, img.src]]));
      };
      img.onerror = () => {
        // Em caso de erro, usa imagem padrão
        const fallback = '/vika_icon_light.gif';
        newBuffer.set(url, fallback);
        setImageBuffer(prev => new Map([...prev, [url, fallback]]));
      };
      img.src = url;
    });
  };
  
  if (!product) return null;

  const isFavorite = favorites.some(fav => fav.productId === product.id);

  // Verifica estoque via selector — mesma lógica do ProductCard
  const selectProdutoById = useMemo(() => selectProductById(product.id), [product.id]);
  const produtoCompleto = useSelector(selectProdutoById);
  const semEstoque = Number(produtoCompleto?.quantidade_estoque ?? 0) <= 0;
  
  useEffect(() => {
    if (open && product?.id) {
      setIsLoadingImages(true);
      
      apiRequestJson('product_images', { params: { id: product.id } })
        .then(response => {
          const mainImage = product.imagem || '/vika_icon_light.gif';
          
          if (response.success && response.data?.length > 0) {
            // Obter imagens adicionais ordenadas e válidas
            const additionalImages = response.data
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(img => img.image_url)
              .filter(img => img && img.trim() !== ''); // Remove URLs vazias ou inválidas
            
            // Criar array único: principal primeiro + adicionais sem duplicatas
            const uniqueImages = [mainImage, ...additionalImages]
              .filter((img, index, arr) => arr.indexOf(img) === index); // Remove duplicatas mantendo ordem
            
            setImages(uniqueImages);
            
            // Pré-carregar todas as imagens no buffer em segundo plano
            if (uniqueImages.length > 1) {
              preloadImages(uniqueImages.slice(1)); // Pré-carrega todas exceto a primeira
            }
          } else {
            // Sem imagens adicionais, usa apenas a principal ou padrão
            setImages([mainImage]);
          }
          
          setIsLoadingImages(false);
        })
        .catch(() => {
          // Em caso de erro, usa imagem principal ou padrão
          const fallbackImage = product.imagem || '/vika_icon_light.gif';
          setImages([fallbackImage]);
          setIsLoadingImages(false);
        });
    }
  }, [open, product?.id, product?.imagem]);
  
  const handleFavoriteClick = () => {
    dispatch(toggleFavorite({
      id: product.id,
      nome: product.nome,
      marca: product.marca,
      imagem: product.imagem,
      categoria: product.categoria
    }));
  };
  
  const handleAddToCart = () => {
    dispatch(addToCart({
      id: product.id,
      nome: product.nome,
      marca: product.marca,
      preco: product.preco,
      imagem: product.imagem,
      categoria: product.categoria
    }));
  };

  const formatDescription = (description) => {
    if (!description) return '';
    return description
      .replace(/&nbsp;g/g, 'g')
      .replace(/&nbsp;/g, ' ')
      .replace(/\\n/g, '\n');
  };
  
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  
  const handleNextImage = () => {
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.8)'
          }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 4 }
        }}>
          <Box sx={{
            flex: { xs: 'none', md: '0 0 300px' },
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.50',
            borderRadius: 1,
            p: 2,
            width: { xs: '100%', md: 300 },
            height: { xs: 250, md: 300 },
            minHeight: { xs: 250, md: 300 },
            maxHeight: { xs: 250, md: 300 },
            overflow: 'hidden'
          }}>
            <Box
              component="img"
              src={imageBuffer.get(images[currentImageIndex]) || images[currentImageIndex] || "/vika_icon_light.gif"}
              alt={product.nome}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                transition: 'opacity 0.2s ease-in-out',
                opacity: isLoadingImages ? 0.7 : 1,
                display: 'block'
              }}
            />
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
                <Box sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5
                }}>
                  {images.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: index === currentImageIndex ? 'primary.main' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
              {product.nome}
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {product.marca}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Categoria: {product.categoria}
            </Typography>
            
            {product.descricao && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Descrição
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    maxHeight: 150,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: 'background.paper'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: 'grey.400',
                      borderRadius: '4px'
                    }
                  }}
                >
                  {formatDescription(product.descricao)}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: 1, mb: 3 }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                {formatPrice(product.preco)}
              </Typography>
              {semEstoque && (
                <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  Indisponível
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
        <IconButton
          onClick={handleFavoriteClick}
          sx={{
            bgcolor: 'rgba(255,255,255,0.8)',
            color: '#f44336',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
          }}
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          {isFavorite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
        </IconButton>
        
        {isMobile ? (
          <Button
            variant="contained"
            onClick={handleAddToCart}
            disabled={semEstoque}
            startIcon={<Box component="img" src="/imagens/icons/svg/vikanaweb-shopping-bag.svg" alt="Carrinho" sx={{ width: 20, height: 20, filter: semEstoque ? 'none' : 'invert(1)' }} />}
            sx={{ flex: 1 }}
          >
            Adicionar
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleAddToCart}
            disabled={semEstoque}
            startIcon={<Box component="img" src="/imagens/icons/svg/vikanaweb-shopping-bag.svg" alt="Carrinho" sx={{ width: 20, height: 20, filter: semEstoque ? 'none' : 'invert(1)' }} />}
          >
            Adicionar ao Carrinho
          </Button>
        )}
        
        <Button onClick={onClose} sx={{ width: { xs: '25%', md: 'auto' } }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProductModal;